import React, { useEffect, useState } from 'react';
import { FormPageLayout, DocStatusPill } from '../../common';
import type { ActionButtonConfig } from '../../common';
import { CarePlanStatus } from '../../../services/patientmr/careplans.service';
import type { CarePlanCertificationPeriod } from '../../../dtos/patientmr/careplans/careplan.dto';
import type { ClinicalTemplate } from '../../../constants/clinicalTemplates';
import { useCarePlanDraft } from '../careplan/hooks/useCarePlanDraft';
import GoalsEditor from '../careplan/goals/GoalsEditor';
import { CarePlanGoal, emptyGoal, goalErrors, newGoalId } from '../careplan/goals/carePlanGoals';
import CertPeriodSection, { applyCertPeriod, hasFixedCertPeriod } from '../careplan/sections/CertPeriodSection';
import TemplateSectionFields from './TemplateSectionFields';
import { templateRequiredErrors } from './templateUtils';

interface Props {
  template: ClinicalTemplate;
  editingId?: string | null;
  patientId?: string;
  existingData?: Record<string, any> | null;
  onSave: (data: any, submitAfterSave?: boolean) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  carePlanStatus?: CarePlanStatus;
  isReadOnly?: boolean;
  onStartQA?: () => void;
  startingQA?: boolean;
  headerExtra?: React.ReactNode;
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  autosaveEnabled?: boolean;
  certificationPeriod?: CarePlanCertificationPeriod | null;
}

const goalsFrom = (data: Record<string, any> | null | undefined): CarePlanGoal[] =>
  Array.isArray(data?.goals) ? (data!.goals as Partial<CarePlanGoal>[]).map(g => ({ ...emptyGoal(), ...g, goalId: g.goalId || newGoalId() })) : [];

/**
 * Phase 20B: care plan from a clinical template (MSW care plan, home health aide care plan).
 * Same props, buttons, autosave and storage as the hand-built care plans: flat data.<key>,
 * data.goals (structured), data.formConfig { templateId, templateVersion }.
 */
const TemplateCarePlanForm: React.FC<Props> = ({
  template, editingId = null, existingData = null, onSave, onReopen, onApprove, onCancel,
  saving = false, submitting = false, reopening = false, carePlanStatus, isReadOnly = false,
  onStartQA, startingQA = false, headerExtra, onAutosave, autosaveEnabled = false, certificationPeriod = null,
}) => {
  const [values, setValues] = useState<Record<string, any>>(() => {
    const { goals: _goals, formConfig: _fc, status: _s, ...rest } = existingData || {};
    return applyCertPeriod({ certificationPeriodFrom: '', certificationPeriodTo: '', ...rest }, certificationPeriod);
  });
  const [goals, setGoals] = useState<CarePlanGoal[]>(() => goalsFrom(existingData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (hasFixedCertPeriod(certificationPeriod)) setValues(prev => applyCertPeriod(prev, certificationPeriod));
  }, [certificationPeriod]);

  const saveData = () => ({
    ...values,
    goals,
    formConfig: { ...(existingData?.formConfig || {}), templateId: template.id, templateVersion: template.version },
    status: 'Draft',
  });

  const validate = () => {
    const next = { ...templateRequiredErrors(template, values), ...goalErrors(goals) };
    setErrors(next);
    return Object.keys(next).filter(k => next[k]).length === 0;
  };

  const handleSave = (submitAfterSave = false) => {
    // Save keeps a DRAFT without checks; Submit validates first (same rule as the other care plans)
    if (!submitAfterSave || validate()) onSave(saveData(), submitAfterSave);
  };

  const onChange = (key: string, value: unknown) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  const isAnyLoading = saving || submitting || reopening || startingQA;
  const isDraft = !carePlanStatus || carePlanStatus === CarePlanStatus.DRAFT;
  const isSubmittedOrQA = carePlanStatus === CarePlanStatus.SUBMITTED || carePlanStatus === CarePlanStatus.QA;
  const isComplete = carePlanStatus === CarePlanStatus.COMPLETE;

  const draft = useCarePlanDraft({
    getSnapshot: () => JSON.stringify({ values, goals }),
    getSaveData: saveData,
    onAutosave,
    enabled: autosaveEnabled && isDraft && !isReadOnly && !!editingId,
    resetKey: `${editingId ?? 'new'}:${carePlanStatus}`,
  });
  const handleClose = () => draft.confirmLeave(onCancel);

  const actions: ActionButtonConfig[] = [
    { key: 'cancel', label: 'Close', onClick: handleClose, variant: 'secondary', disabled: isAnyLoading },
    { key: 'save', label: 'Save', loadingLabel: editingId ? 'Updating...' : 'Saving...', onClick: () => handleSave(false), variant: 'primary', disabled: isAnyLoading, loading: saving, hidden: !isDraft || isReadOnly },
    { key: 'submit', label: 'Submit', loadingLabel: 'Submitting...', onClick: () => handleSave(true), variant: 'primary', disabled: isAnyLoading, loading: submitting, hidden: !isDraft || isReadOnly },
    { key: 'reject', label: 'Reject', loadingLabel: 'Rejecting...', onClick: onReopen ?? (() => {}), variant: 'danger', disabled: isAnyLoading, loading: reopening, hidden: !isSubmittedOrQA || !onReopen || isReadOnly },
    { key: 'startQA', label: 'Start QA', loadingLabel: 'Starting QA...', onClick: onStartQA ?? (() => {}), variant: 'warning', disabled: isAnyLoading, loading: startingQA, hidden: carePlanStatus !== CarePlanStatus.SUBMITTED || !onStartQA || isReadOnly },
    { key: 'approve', label: 'Approve', loadingLabel: 'Approving...', onClick: onApprove ?? (() => {}), variant: 'success', disabled: isAnyLoading, loading: submitting, hidden: !isSubmittedOrQA || !onApprove || isReadOnly },
    { key: 'reopen', label: 'Reopen', loadingLabel: 'Reopening...', onClick: onReopen ?? (() => {}), variant: 'primary', disabled: isAnyLoading, loading: reopening, hidden: !isComplete || !onReopen || isReadOnly, className: 'bg-blue-600 hover:bg-blue-700' },
  ];

  const missingCount = Object.values(errors).filter(Boolean).length;

  return (
    <FormPageLayout
      onBack={handleClose}
      actions={actions}
      title={template.title}
      headerRight={<div className="flex items-center gap-2">{draft.indicator}{headerExtra}<DocStatusPill status={carePlanStatus} /></div>}
    >
      <div {...draft.captureProps} className="space-y-4 text-left">
        {missingCount > 0 && (
          <p className="text-sm text-red-700" role="alert">Complete the {missingCount} highlighted {missingCount === 1 ? 'field' : 'fields'} before submitting.</p>
        )}
        <CertPeriodSection
          fixedPeriod={certificationPeriod}
          variant="card"
          title="Certification Period"
          values={values}
          onChange={onChange as (field: string, value: string | boolean) => void}
          errors={errors}
          readOnly={isReadOnly}
          collapsed={Boolean(collapsed.certification)}
          onToggle={() => setCollapsed(prev => ({ ...prev, certification: !prev.certification }))}
        />
        {template.sections.map(section => (
          <TemplateSectionFields
            key={section.name}
            template={template}
            section={section}
            values={values}
            onChange={onChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={Boolean(collapsed[section.name])}
            onToggle={() => setCollapsed(prev => ({ ...prev, [section.name]: !prev[section.name] }))}
          />
        ))}
        <GoalsEditor
          discipline={template.discipline}
          domains={template.goalDomains}
          goals={goals}
          onChange={setGoals}
          errors={errors}
          readOnly={isReadOnly}
          collapsed={Boolean(collapsed.goals)}
          onToggle={() => setCollapsed(prev => ({ ...prev, goals: !prev.goals }))}
        />
      </div>
      {draft.dialog}
    </FormPageLayout>
  );
};

export default TemplateCarePlanForm;
